// Human-readable URLs for products and blog posts.
//
// Product pages used to be linked purely by Firestore document id, so Google
// indexed /product/80R7lTVof0QZl1VlhSOp — a URL with no keywords in it at all.
// Products now carry a `slug` field (generated in admin → Products) and every
// link goes through productPath(), which falls back to the id whenever a
// product hasn't been given a slug yet. Nothing breaks mid-migration.

// A Firestore auto-id is exactly 20 characters of [A-Za-z0-9]. Slugs are
// lowercase and almost always contain a hyphen, so this cleanly decides
// whether a :id route param should be looked up as a document id or as a slug
// — which saves a wasted getDoc on every slug-based product page.
export function looksLikeDocId(value) {
  return typeof value === 'string' && /^[A-Za-z0-9]{20}$/.test(value);
}

// Lowercase, ASCII, hyphen-separated. Keeps digits: product names here carry
// their price ("beach bags  @1800"), and dropping the number would collapse
// "beach bags @1800" and "beach bags @2000" onto the same slug.
export function slugify(value) {
  return String(value == null ? '' : value)
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')  // strip accents
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/['’]/g, '')             // don't turn "men's" into "men-s"
    .replace(/[^a-z0-9]+/g, '-')       // @ / spaces / punctuation → separator
    .replace(/^-+|-+$/g, '')
    .slice(0, 80)
    .replace(/-+$/g, '');
}

// Slug for one product, uniqueness-checked against slugs already in use.
// `taken` is a Set of slugs claimed by *other* products; collisions get -2, -3…
// Falls back to the document id if the name slugifies to nothing (e.g. a name
// that is entirely punctuation).
export function uniqueSlug(name, taken = new Set(), fallback = '') {
  const base = slugify(name) || slugify(fallback) || 'item';
  if (!taken.has(base)) return base;
  for (let n = 2; n < 500; n += 1) {
    const candidate = `${base}-${n}`;
    if (!taken.has(candidate)) return candidate;
  }
  return `${base}-${Date.now()}`;
}

// The canonical public path for a product. ALWAYS use this to build a product
// link — never interpolate `product.id` directly, or the page will disagree
// with its own canonical tag and split ranking between two URLs.
export function productPath(product) {
  if (!product) return '/shop';
  return `/product/${product.slug || product.id}`;
}

export function blogPath(post) {
  if (!post) return '/blog';
  return `/blog/${post.slug || post.id}`;
}
